import { useState, type ReactNode } from 'react'
import { SafetyOutlined } from '@ant-design/icons'
import { ConfigProvider, Card, Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { welcomeText, type PortalBranding } from '@/features/portal/api'
import { BrandLogo } from './BrandLogo'
import { LanguageSwitcher } from './LanguageSwitcher'

interface PortalShellProps {
  /** Ob'ekt nomi — bo'lmasa (masalan yuklanish/xato holatida) sarlavha yashiriladi */
  venueName?: string
  zoneName?: string
  /**
   * Ob'ekt sozlagan ko'rinish. `null`/berilmagan — Netlink palitrasi va
   * logotipi (`app/theme/tokens.ts`), ya'ni bu qiymat majburiy emas:
   * yuklanish va xato ekranlari uni umuman bilmaydi.
   */
  branding?: PortalBranding | null
  /** Zona hali yuklanmoqda: logotip o'rni bo'sh turadi, aks holda ob'ekt logotipidan oldin Netlink logotipi ko'rinib qolardi. */
  pending?: boolean
  children: ReactNode
}

const LOGO_SLOT_HEIGHT = 56

/**
 * Bitta ekran = bitta karta — vazifa talabi ("yirik tugmalar, minimal
 * matn, bitta ekranda bitta qadam"). Til tanlash har doim yuqorida,
 * o'ng burchakda — mehmon noto'g'ri tilda ochilib qolgan bo'lsa,
 * BIRINCHI navbatda shuni topishi kerak.
 *
 * <h2>Ob'ekt brendi</h2>
 * Logotip, rang, salomlashish, qo'llab-quvvatlash telefoni va shartlar
 * havolasi `venue.portal_template` dan keladi va `GET /api/portal/zone`
 * javobida `template` maydonida qaytadi. Hech biri MAJBURIY emas:
 * sozlanmagan ob'ektda barchasi tushib qoladi va portal Netlink logotipi
 * hamda palitrasi bilan ishlaydi.
 *
 * ⚠️ Rang alohida `ConfigProvider` bilan qo'llanadi, ildizdagisi
 * ustidan. Sabab: rang zona so'rovi YUKLANGANDAN keyin ma'lum bo'ladi,
 * ildiz provayderi esa router'dan yuqorida turadi va o'sha paytda hali
 * hech narsa bilmaydi. Ichkarida qo'llash yorug'/qorong'i algoritmni
 * ham buzmaydi — u meros bo'lib o'tadi.
 */
export function PortalShell({ venueName, zoneName, branding, pending = false, children }: PortalShellProps) {
  const { t, i18n } = useTranslation()
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null)
  const welcome = welcomeText(branding ?? null, i18n.resolvedLanguage ?? 'uz')
  const logoUrl = branding?.logoUrl ?? null
  const venueLogoUrl = logoUrl !== failedLogoUrl ? logoUrl : null
  const primaryColor = branding?.primaryColor ?? null

  const shell = (
    <div className="xsib-portal-shell">
      <div className="xsib-portal-card">
        <Flex justify="flex-end" style={{ marginBottom: 8 }}>
          <LanguageSwitcher />
        </Flex>
        <Card bordered={false} styles={{ body: { padding: 24 } }}>
          <Flex justify="center" align="center" style={{ height: LOGO_SLOT_HEIGHT, marginBottom: 16 }}>
            {pending ? null : venueLogoUrl ? (
              /*
                `alt` — ob'ekt nomi, "logo" emas: ekranni o'qib beruvchi
                dastur mehmonga qayerda ekanini aytsin. Havola tashqi va
                yuklanmasligi mumkin — buzuq rasm belgisi o'rniga Netlink
                logotipi qaytadi.
              */
              <img
                src={venueLogoUrl}
                alt={venueName ?? ''}
                referrerPolicy="no-referrer"
                style={{ maxHeight: LOGO_SLOT_HEIGHT, maxWidth: '70%', objectFit: 'contain' }}
                onError={() => setFailedLogoUrl(venueLogoUrl)}
              />
            ) : (
              <BrandLogo height={32} label={t('app.name')} />
            )}
          </Flex>

          {venueName ? (
            <Flex vertical gap={2} style={{ marginBottom: 20, textAlign: 'center' }}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {venueName}
              </Typography.Title>
              {/* Yorliqsiz "Lobby 2F" mehmonga hech narsa demaydi — u
                  ob'ekt nomining ostidagi bu qatorning TARMOQ nomi
                  ekanini bilmaydi. */}
              {zoneName ? (
                <Typography.Text type="secondary">{t('entry.network', { zoneName })}</Typography.Text>
              ) : null}
              {welcome ? (
                <Typography.Paragraph style={{ margin: '8px 0 0' }}>{welcome}</Typography.Paragraph>
              ) : null}
            </Flex>
          ) : null}

          {children}

          {/*
            ⚠️ XAVFSIZLIK ESLATMASI — MEHMON PORTALIDA U ODDIY
            BEZAKDAN KO'RA KO'PROQ KERAK.

            Soxta Wi-Fi kirish sahifasi — keng tarqalgan hujum: hujumchi
            o'z nuqtasini ochadi, mehmonxonanikiga o'xshash sahifa
            ko'rsatadi va karta ma'lumotini so'raydi. Mehmon esa aynan
            shu paytda eng ishonuvchan holatda: u "internetga kirish
            uchun shunday bo'lsa kerak" deb o'ylaydi.

            Shuning uchun bu qator BIZ hech qachon nima
            so'ramasligimizni aytadi — mehmon soxta sahifani shu bilan
            farqlay oladi. Uni "yordam" bo'limiga qo'yish befoyda:
            u yerga hech kim kirmaydi.

            ⚠️ Ob'ekt brendi bilan RAQOBATLASHMAYDI: matn kichik,
            ikkilamchi rangda va kartaning eng pastida. Portal
            mehmonxonaning yuzi bo'lib qolishi kerak, biznikiga
            aylanmasligi.
          */}
          <Flex gap={6} align="flex-start" style={{ marginTop: 20 }}>
            <SafetyOutlined style={{ marginTop: 3, opacity: 0.55 }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('brand.safetyNotice')}
            </Typography.Text>
          </Flex>

          {branding && (branding.supportPhone || branding.termsUrl) ? (
            <Flex
              vertical
              gap={4}
              style={{ marginTop: 20, textAlign: 'center' }}
              className="xsib-portal-brand-footer"
            >
              {branding.supportPhone ? (
                <Typography.Text type="secondary">
                  {t('brand.support')}{' '}
                  {/* `tel:` — mehmon telefonda, raqamni ko'chirib yozmasin */}
                  <Typography.Link href={`tel:${branding.supportPhone.replace(/[^\d+]/g, '')}`}>
                    {branding.supportPhone}
                  </Typography.Link>
                </Typography.Text>
              ) : null}
              {branding.termsUrl ? (
                <Typography.Link
                  href={branding.termsUrl}
                  target="_blank"
                  rel="noopener noreferrer external"
                >
                  {t('brand.terms')}
                </Typography.Link>
              ) : null}
            </Flex>
          ) : null}
        </Card>
      </div>
    </div>
  )

  if (!primaryColor) return shell
  return (
    <ConfigProvider theme={{ token: { colorPrimary: primaryColor, colorInfo: primaryColor, colorLink: primaryColor } }}>
      {shell}
    </ConfigProvider>
  )
}
